import { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSignalR } from '@hooks/useSignalR';
import { apiClient } from '@services/api';
import './MiniChat.scss';

interface Message {
  id: string;
  senderType: string;
  senderName?: string;
  content: string;
  sentAt: string;
  metadata?: string | null;
}

/** Detects messages that carry a status tracker, menu picker, or laundry picker payload. */
function hasRichPayload(raw?: string | null): boolean {
  if (!raw) return false;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return false;
    return parsed.type === 'menu' || parsed.type === 'laundry_menu' || !!parsed.referenceId;
  } catch { return false; }
}

interface MiniChatProps {
  hotelSlug?: string | null;
}

/** The message as plain text for the preview: markdown emphasis marks (**bold**, __bold__) and heading
 *  hashes dropped — the full message page renders them. */
const plainPreview = (text: string) =>
  (text || '').replace(/\*\*(.+?)\*\*/g, '$1').replace(/__(.+?)__/g, '$1').replace(/^#{1,6}\s+/gm, '');

/** The last message shown, for the session (token) it was loaded for — so the welcome page re-shows it at once. */
let lastShown: { token: string | null; message: Message | null } | null = null;
const currentToken = () => { try { return localStorage.getItem('gcxp_token'); } catch { return null; } };

export default function MiniChat({ hotelSlug }: MiniChatProps) {
  const navigate = useNavigate();
  const messagesPath = hotelSlug ? `/${hotelSlug}/messages` : '/messages';
  const { on } = useSignalR();
  const cached = lastShown && lastShown.token === currentToken() ? lastShown : null;
  const [lastMessage, setLastMessage] = useState<Message | null>(cached?.message ?? null);
  const [loaded, setLoaded] = useState(cached !== null);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  // The preview text fades out at the bottom only when the message is longer than the lines shown.
  const previewTextRef = useRef<HTMLSpanElement>(null);
  const [previewCut, setPreviewCut] = useState(false);
  useLayoutEffect(() => {
    const el = previewTextRef.current;
    setPreviewCut(!!el && el.scrollHeight > el.clientHeight + 1);
  }, [lastMessage]);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const loadMessagesRef = useRef<() => void>(() => {});
  loadMessagesRef.current = () => {
    apiClient.get<{ messages: Message[] }>('/api/v1/guest/messages/conversation')
      .then(({ data }) => {
        const msgs = data.messages || [];
        const last = msgs.length > 0 ? msgs[msgs.length - 1] : null;
        lastShown = { token: currentToken(), message: last };
        setLastMessage(last);
      })
      .catch(() => {})
      .finally(() => setLoaded(true));
  };

  // Load last message on mount
  useEffect(() => { loadMessagesRef.current(); }, []);

  // Reload when returning to browser
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') loadMessagesRef.current();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  // Listen for new messages via SignalR — reload from server so metadata
  // (status/menu cards) is always present, not just what the event carries.
  useEffect(() => {
    const unsub = on('NewMessage', () => loadMessagesRef.current());
    return () => unsub();
  }, [on]);

  const sendMessage = async () => {
    const content = input.trim();
    if (!content || sending) return;

    setSending(true);
    setInput('');

    // Optimistic
    setLastMessage({ id: `temp-${Date.now()}`, senderType: 'Guest', content, sentAt: new Date().toISOString() });

    try {
      await apiClient.post('/api/v1/guest/messages/send', { content });
    } catch {
      setInput(content);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Voice recording
  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4';
      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(track => track.stop());
        const blobMimeType = mimeType.split(';')[0];
        const audioBlob = new Blob(audioChunksRef.current, { type: blobMimeType });
        audioChunksRef.current = [];
        if (audioBlob.size === 0) return;

        setIsTranscribing(true);
        try {
          const ext = mimeType.includes('webm') ? '.webm' : '.m4a';
          const formData = new FormData();
          formData.append('file', audioBlob, `recording${ext}`);
          const { data } = await apiClient.post<{ success: boolean; transcription?: string }>('/api/v1/guest/messages/transcribe', formData);
          if (data.success && data.transcription) {
            setInput(prev => (prev.trim() ? prev + ' ' : '') + data.transcription);
            setTimeout(() => inputRef.current?.focus(), 50);
          }
        } catch { /* ignore */ } finally {
          setIsTranscribing(false);
        }
      };

      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start(250);
      setIsRecording(true);
      setRecordingDuration(0);
      recordingTimerRef.current = setInterval(() => setRecordingDuration(prev => prev + 1), 1000);
    } catch { /* mic access denied */ }
  }, []);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current?.state !== 'inactive') mediaRecorderRef.current?.stop();
    mediaRecorderRef.current = null;
    setIsRecording(false);
    setRecordingDuration(0);
    if (recordingTimerRef.current) { clearInterval(recordingTimerRef.current); recordingTimerRef.current = null; }
  }, []);

  const cancelRecording = useCallback(() => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.onstop = () => { mediaRecorderRef.current = null; };
      if (mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stream.getTracks().forEach(t => t.stop());
        mediaRecorderRef.current.stop();
      }
    }
    audioChunksRef.current = [];
    mediaRecorderRef.current = null;
    setIsRecording(false);
    setRecordingDuration(0);
    if (recordingTimerRef.current) { clearInterval(recordingTimerRef.current); recordingTimerRef.current = null; }
  }, []);

  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current?.state !== 'inactive') {
        mediaRecorderRef.current?.stream.getTracks().forEach(t => t.stop());
        mediaRecorderRef.current?.stop();
      }
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
    };
  }, []);

  const formatTime = (s: number) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;

  const formatMsgTime = (iso: string) => {
    try {
      return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch { return ''; }
  };

  if (!loaded) {
    return (
      <div className="mini-chat-card card shadow-sm mb-4 placeholder-glow">
        <div className="mini-chat-header">
          <span className="mini-chat-icon">💬</span>
          <span className="mini-chat-title">Messages</span>
        </div>
        <div className="mini-chat-preview">
          <span className="placeholder col-9 d-block" style={{ height: '2rem', borderRadius: '0.75rem' }} />
        </div>
        <div style={{ padding: '0.5rem 0.75rem', display: 'flex', gap: '0.35rem' }}>
          <span className="placeholder" style={{ flex: 1, height: '36px', borderRadius: '1.25rem' }} />
          <span className="placeholder rounded-circle" style={{ width: '34px', height: '34px' }} />
        </div>
      </div>
    );
  }

  return (
    <div className="mini-chat-card card shadow-sm mb-4">
      <div className="mini-chat-header" onClick={() => navigate(messagesPath)}>
        <span className="mini-chat-icon">💬</span>
        <span className="mini-chat-title">Messages</span>
        <svg className="mini-chat-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="9 18 15 12 9 6"/></svg>
      </div>

      {/* Last message preview — a fixed height whatever it holds (loading, none, short, long), so the
          welcome page below it never moves (scroll is restored to the same spot on back). */}
      {lastMessage ? (
        <div className="mini-chat-preview" onClick={() => navigate(messagesPath)}>
          {lastMessage.senderType !== 'Guest' && lastMessage.senderName && (
            <span className="mini-chat-sender">{lastMessage.senderName}</span>
          )}
          <span className={`mini-chat-bubble ${lastMessage.senderType === 'Guest' ? 'sent' : 'received'}`}>
            <span ref={previewTextRef} className={`mini-chat-bubble-text${previewCut ? ' cut' : ''}`}>
              {plainPreview(lastMessage.content)}
            </span>
          </span>
          <div className="mini-chat-meta">
            {hasRichPayload(lastMessage.metadata) && (
              <button
                className="mini-chat-view-full"
                onClick={(e) => { e.stopPropagation(); navigate(messagesPath); }}
              >
                View full message
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="9 18 15 12 9 6"/></svg>
              </button>
            )}
            <span className="mini-chat-time">{formatMsgTime(lastMessage.sentAt)}</span>
          </div>
        </div>
      ) : (
        <div className="mini-chat-preview mini-chat-empty" onClick={() => navigate(messagesPath)}>
          No messages yet — ask us anything.
        </div>
      )}

      {/* Input area */}
      <div className="mini-chat-input-area">
        {isRecording ? (
          <div className="mini-chat-recording">
            <span className="recording-dot" />
            <span className="recording-time">{formatTime(recordingDuration)}</span>
            <button className="mini-chat-btn" onClick={cancelRecording}>✕</button>
            <button className="mini-chat-btn stop" onClick={stopRecording}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><rect x="4" y="4" width="16" height="16" rx="2" /></svg>
            </button>
          </div>
        ) : (
          <textarea
            ref={inputRef}
            className="mini-chat-textarea"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={isTranscribing ? 'Transcribing...' : 'Type a message...'}
            rows={1}
            maxLength={1000}
            disabled={sending || isTranscribing}
          />
        )}
        {!isRecording && (
          <button className={`mini-chat-btn mic${isTranscribing ? ' transcribing' : ''}`} onClick={startRecording} disabled={sending || isTranscribing}>
            {isTranscribing ? (
              <span className="mic-spinner" />
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                <path d="M19 10v2a7 7 0 0 1-14 0v-2" /><line x1="12" y1="19" x2="12" y2="23" /><line x1="8" y1="23" x2="16" y2="23" />
              </svg>
            )}
          </button>
        )}
        <button className="mini-chat-btn send" onClick={sendMessage} disabled={!input.trim() || sending || isRecording}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
          </svg>
        </button>
      </div>
    </div>
  );
}
